/**
 * Criterion data structure.
 *
 * Used to perform a comparison with preset operator
 * and right operand and then, if the predicate is truthy, will return
 * the associated value.
 *
 * @example
 * // Pseudo-code :
 * if (<leftOperand> <criterion.predicate.operator> <criterion.predicate.rightOperand>) {
 *   return <criterion.value>;
 * }
 */
export type Criterion<T, U> = {
  /**
   * Predicate to validate the criterion.
   */
  predicate: {
    /**
     * Operator of the predicate.
     */
    operator: "!==" | "<" | "<=" | "===" | ">" | ">=";
    /**
     * Right operand of the predicate.
     */
    rightOperand: U;
  };
  /**
   * Value associated with the criterion.
   */
  value: T;
};

export function evaluateCriterion<T, U>(
  leftOperand: U,
  { predicate, value }: Criterion<T, U>
): T | undefined {
  switch (predicate.operator) {
    case "!==":
      if (leftOperand !== predicate.rightOperand) return value;
      break;
    case "<":
      if (leftOperand < predicate.rightOperand) return value;
      break;
    case "<=":
      if (leftOperand <= predicate.rightOperand) return value;
      break;
    case "===":
      if (leftOperand === predicate.rightOperand) return value;
      break;
    case ">":
      if (leftOperand > predicate.rightOperand) return value;
      break;
    case ">=":
      if (leftOperand >= predicate.rightOperand) return value;
      break;
    default:
      throw new Error(`Unhandled predicate operator: ${predicate.operator}`);
  }

  return;
}

export default evaluateCriterion;
